/**
 * ParsedTicketsProvider — parses the enriched SIM ticket export ONCE, above the Tickets tabs,
 * and shares the result via context.
 *
 * WHY: enrichedListToParsed runs ~5 moment() ops per ticket (~20k+ across the dataset). Previously
 * each tab's hook (useSimHealthPreview, useSimOeDashboard, TimeSpentDetailsTab) parsed the full
 * set independently, and some did so inside a memo keyed on the active filters — so every filter
 * change (and every kept-alive tab) re-parsed everything on the main thread, freezing the UI on
 * large ranges. Parsing here, memoized on `enriched.tickets` alone, means:
 *   - parse happens ONCE per data load (not per filter change, not per tab),
 *   - tabs can unmount freely (parsed data + fetch state live here) — no keepAlive needed,
 *   - each tab only does the cheap applySimTicketFilters on the shared parsed array.
 */
import React, { createContext, useContext, useMemo } from 'react';
import moment from 'moment-timezone';
import { TicketsParsedDataItem } from '../../tickets/TicketsInterfaces';
import { DashboardInsight, EnrichedTicket, OeOpportunity } from '../../sim-ticket-health-preview/interfaces/enrichedTicketExport.types';
import { enrichedListToParsed } from '../../sim-ticket-health-preview/utils/enrichedAdapter';
import { useEnrichedTickets } from '../../sim-ticket-health-preview/hooks/useEnrichedTickets';

export interface ParsedTicketsContextValue {
  status: 'loading' | 'finished' | 'error';
  /** The full enriched population, parsed to TicketsParsedDataItem — parsed ONCE. */
  parsedAll: TicketsParsedDataItem[];
  /** Raw enriched tickets indexed by case_id (fallback sim_issue_alias) for LLM-overlay lookups. */
  byId: Map<string, EnrichedTicket>;
  /** Stable "now" used for parsing/scoring, so age/derived values don't drift between reads. */
  asOf: moment.Moment;
  oeOpportunities: OeOpportunity[];
  dashboardInsights: Record<string, DashboardInsight>;
  generatedAt: string | undefined;
  lastUpdatedAt: string | null;
}

const EMPTY: ParsedTicketsContextValue = {
  status: 'loading',
  parsedAll: [],
  byId: new Map(),
  asOf: moment(0),
  oeOpportunities: [],
  dashboardInsights: {},
  generatedAt: undefined,
  lastUpdatedAt: null
};

const ParsedTicketsContext = createContext<ParsedTicketsContextValue>(EMPTY);

export const ParsedTicketsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enriched = useEnrichedTickets();

  const value = useMemo<ParsedTicketsContextValue>(() => {
    const asOf = moment();
    // Parse + index ONCE, keyed on the ticket data only (NOT on filters).
    const parsedAll = enriched.tickets.length > 0 ? enrichedListToParsed(enriched.tickets, asOf) : [];
    const byId = new Map(enriched.tickets.map((t) => [t.case_id || t.sim_issue_alias, t]));
    return {
      status: enriched.status,
      parsedAll,
      byId,
      asOf,
      oeOpportunities: enriched.oeOpportunities,
      dashboardInsights: enriched.dashboardInsights,
      generatedAt: enriched.generatedAt,
      lastUpdatedAt: enriched.lastUpdatedAt
    };
    // Re-parse ONLY when the underlying ticket data (or load status) changes — never on filters.
  }, [enriched.status, enriched.tickets, enriched.oeOpportunities, enriched.dashboardInsights, enriched.generatedAt, enriched.lastUpdatedAt]);

  return <ParsedTicketsContext.Provider value={value}>{children}</ParsedTicketsContext.Provider>;
};

/** Consume the once-parsed enriched ticket population. */
export const useParsedEnrichedTickets = (): ParsedTicketsContextValue => useContext(ParsedTicketsContext);
